<template>
  <main-template>
    <v-row>
      <v-col>
        <span class="text-h4">Componentes Curriculares</span>
        <e-help-tooltip :text="$constants.tooltips.turmas.criarDisciplina" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pt-0 pb-0">
        <div class="d-flex mb-2 justify-space-between">
          <v-btn class="mr-2" color="success" @click="criarDisciplina">
            Criar Componente Curricular
          </v-btn>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            autofocus
            class="ml-2 search-input"
            dense
            hide-details
            label="Pesquisar..."
            single-line
            solo
          />
        </div>
      </v-col>

      <v-col class="pt-0">
        <v-data-table
          :custom-filter="dataTableFilter"
          :headers="table.headers"
          :items="disciplinasList"
          :items-per-page="10"
          :loading="carregandoDisciplinas"
          :search="search"
          class="elevation-1"
          hide-default-footer
        >
          <template v-slot:body="{ items }">
            <tbody is="transition-group" duration="150" name="scroll-x-transition">
              <tr
                v-for="disciplina of items"
                :key="disciplina.id"
                :class="disciplina.estaDeletado ? ['deleted'] : []"
              >
                <td>{{ disciplina.id }}</td>
                <td>{{ disciplina.codigo || "- - -" }}</td>

                <td>
                  {{ disciplina.descricao }}

                  <v-tooltip v-if="isComponenteINEP(disciplina.codigo)" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-icon v-bind="attrs" v-on="on" class="ml-1" color="primary" small>
                        mdi-information
                      </v-icon>
                    </template>
                    <span>{{ getInepTooltip() }}</span>
                  </v-tooltip>
                </td>

                <td>
                  <v-tooltip v-if="!disciplina.estaDeletado" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        :disabled="isComponenteINEP(disciplina.codigo)"
                        class="ml-1"
                        color="blue-grey lighten-3"
                        x-small
                        @click="() => editarDisciplina(disciplina)"
                      >
                        <v-icon small>fa fa-edit</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar Componente Curricular</span>
                  </v-tooltip>

                  <v-tooltip v-if="!disciplina.estaDeletado" bottom color="error">
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        :disabled="isComponenteINEP(disciplina.codigo)"
                        class="ml-1"
                        color="error"
                        x-small
                        @click="() => deletarDisciplina(disciplina)"
                      >
                        <v-icon small>fa fa-trash</v-icon>
                      </v-btn>
                    </template>
                    <span>Apagar Componente Curricular</span>
                  </v-tooltip>

                  <v-tooltip v-if="disciplina.estaDeletado" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        :disabled="isComponenteINEP(disciplina.codigo)"
                        class="ml-1"
                        color="info"
                        x-small
                        @click="() => recuperarDisciplina(disciplina)"
                      >
                        <v-icon small>fa fa-sync</v-icon>
                      </v-btn>
                    </template>
                    <span>Recuperar Componente Curricular</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>

        <e-paginate
          :current_page="paginate.current_page"
          :last_page="paginate.last_page"
          :pageNumber="pageNumber"
          :search="search"
          @changeActionResponse="listarDisciplinas"
        />
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import { dataTableFilter } from "@/plugins/searchQuery";
import Disciplina from "@/Models/Disciplina";

export default {
  computed: {
    ...mapGetters("Disciplinas", ["carregandoDisciplinas", "disciplinasList"]),
  },

  data() {
    return {
      dataTableFilter,
      disciplinasList: [],
      pageNumber: 0,
      paginate: {
        current_page: 1,
        last_page: 0,
      },
      search: "",
      table: {
        headers: [
          { text: "#", value: "id" },
          { text: "Código", value: "codigo" },
          { text: "Nome", value: "descricao" },
          { text: "Ações", value: "actions", sortable: false },
        ],
      },
    };
  },

  mounted() {
    this.carregandoDisciplinas = true;
    this.$loaderService.open("Carregando Componentes Curriculares");
    this.$store.dispatch("Disciplinas/loadDisciplinas");
    this.listarDisciplinas(this.pageNumber);
    this.carregandoDisciplinas = false;
    this.$loaderService.close();
  },

  methods: {
    criarDisciplina() {
      this.$router.push({ name: "disciplinas.create" });
    },

    async deletarDisciplina(disciplina) {
      if (this.isComponenteINEP(disciplina.codigo)) return;

      this.$modals.danger({
        buttons: [
          {
            callback: () => this.$modals.close(),
            props: {
              color: "warning",
              small: true,
            },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Deletando o Componente Curricular");
              try {
                await this.$services.disciplinasService.deletarDisciplina(disciplina);
                this.$toast.success("Componente Curricular deletado com sucesso");
                await this.listarDisciplinas();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: {
              color: "error",
              small: true,
            },
            text: "Confirmar",
          },
        ],
        confirmationCode: disciplina.id,
        confirmationMessage: `Por favor, digite <strong>${disciplina.id}</strong> para confirmar`,
        message: "Tem certeza que deseja apagar este Componente Curricular?",
        title: "Cuidado!",
      });
    },

    editarDisciplina(disciplina) {
      if (this.isComponenteINEP(disciplina.codigo)) return;

      this.$router.push({
        name: "disciplinas.edit",
        params: { disciplina_id: disciplina.id },
      });
    },

    getInepTooltip() {
      return "Este Componente Curricular é proveniente do Censo Escolar e, por isso, não pode ser editado ou excluído.";
    },

    isComponenteINEP(codigo) {
      if (!codigo) return false;
      return this.$constants.componentesPadraoINEP.includes(String(codigo).trim());
    },

    async listarDisciplinas(pageNumber, query = null) {
      this.carregandoDisciplinas = true;
      try {
        const response = await this.$services.disciplinasService.syncAllPaginated(
          pageNumber,
          query
        );
        this.disciplinasList = response.data.map((disciplina) => new Disciplina(disciplina));
        this.paginate = response;
      } catch (error) {
        this.$handleError(error);
      }
      this.carregandoDisciplinas = false;
    },

    recuperarDisciplina(disciplina) {
      this.$modals.warn({
        buttons: [
          {
            callback: () => this.$modals.close(),
            props: {
              color: "warning",
              small: true,
            },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Recuperando a Disciplina");
              try {
                await this.$services.disciplinasService.recuperarDisciplina(disciplina);
                this.$toast.success("Disciplina recuperada com sucesso");
                await this.listarDisciplinas();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: {
              color: "error",
              small: true,
            },
            text: "Confirmar",
          },
        ],
        confirmationCode: disciplina.id,
        confirmationMessage: `Por favor, digite <strong>${disciplina.id}</strong> para confirmar`,
        message: "Tem certeza que deseja recuperar esta disciplina?",
        title: "Atenção!",
      });
    },
  },
};
</script>

<style></style>
